<template>
  <CenteredLayout id="signin">
    <div>
      <h1>Take your Research<br />to the Next Level</h1>

      <ul class="points">
        <li><font-awesome-icon icon="check" /> Collect your data securely</li>
        <li><font-awesome-icon icon="check" /> Easy statistical analysis</li>
        <li><font-awesome-icon icon="check" /> Charts and texts for your article</li>
      </ul>
    </div>

    <div v-if="step < 3">
      <form
        class="box"
        role="form"
        method="post"
        v-if="step == 1"
        @submit.prevent="checkFirstForm"
        aria-label="Registration form"
      >
        <h2 style="margin-top: 0">Create your free account</h2>

        <slot></slot>

        <Messages :messages="messages" />

        <div class="entry">
          <label for="Email">Professional email:</label>
          <div style="position: relative">
            <font-awesome-icon class="input-warn" icon="exclamation-circle" v-show="is_email_exists && !shared_user" />
            <input
              type="email"
              :class="['form-control', is_email_exists && !shared_user ? 'error' : '', shared_user ? 'readonly-input' : '']"
              placeholder="Professional email"
              name="Email"
              id="Email"
              v-model="email"
              @change="on_email_change($event)"
              :readonly="!!shared_user"
              required
              autofocus
            />
            <div v-show="is_email_exists && !shared_user" class="error">
              This account already exists.
              <a @click.prevent="password_recall">Lost the password ?</a>
            </div>
            <div v-if="shared_user" class="shared-user-info">
              <font-awesome-icon icon="info-circle" style="color: var(--blue-info-color, #17a2b8); margin-right: 0.5em;" />
              Welcome {{ shared_user.FirstName }}! Please set your password to complete your account.
            </div>
          </div>
        </div>

        <div style="margin-top: 2em">
          <label for="Password">Password:</label>
          <PasswordInput
            name="new-password"
            :password="password"
            @change="password = $event"
            @error="is_password_bad = $event"
          ></PasswordInput>
        </div>

        <div class="entry">
          <label for="Profession">Profession:</label>
          <select id="profession" v-model="Profession" class="form-control" required>
            <option></option>
            <option value="Doctor">Doctor</option>
            <option value="Medicine resident">Medicine resident (interne)</option>
            <option value="Research assistant">Research assistant</option>
            <option value="Statistician">Statistician</option>
            <option value="Pharmaceutic professional">Pharmaceutic professional</option>
            <option value="Device manufacturer">Device manufacturer</option>
            <option value="Other">My profession is not in the list</option>
          </select>
        </div>

        <div style="margin-top: 2em">
          <input type="checkbox" name="terms" id="terms" v-model="tou" style="margin-bottom: 1em" required />
          <label for="terms" style="font-weight: normal"
            >I have read and I accept the
            <a href="https://www.easymedstat.com/terms-of-use">terms of use</a>
            of the service.</label
          >
        </div>

        <div style="text-align: center">
          <button
            class="btn btn-primary"
            type="submit"
            style="margin-left: 1em"
            :disabled="(is_email_exists && !shared_user) || is_password_bad"
          >
            Create my account
          </button>
        </div>
      </form>
      <div style="text-align: right" v-show="step == 1">
        <router-link to="/login">Already have an account ? Sign-in</router-link>
      </div>
      <form class="box" role="form" method="post" v-if="step == 2" @submit="signUp" aria-label="Registration form 2">
        <h2 style="margin-top: 0">Step 2/2</h2>

        <Messages :messages="messages" />

        <div>
          <label for="given-name">First name:</label>
          <input
            type="text"
            class="form-control"
            placeholder="Your first name"
            name="given-name"
            ref="first_name_input"
            v-model.trim="FirstName"
            minlength="2"
            required
            autofocus
          />
        </div>

        <div style="margin-top: 2em">
          <label for="family-name">Last name:</label>
          <input
            type="text"
            class="form-control"
            placeholder="Your last name"
            name="family-name"
            v-model.trim="LastName"
            minlength="2"
            required
          />
        </div>

        <div>
          <label for="country">Country:</label>
          <CountrySelector v-model="CountryCode"></CountrySelector>
        </div>

        <div>
          <label for="phone">Professional phone:</label>
          <input
            type="tel"
            class="form-control"
            placeholder="+55 55 55 55 55"
            pattern="^\+*[0-9 ,\.\-]+$"
            name="phone"
            v-model.trim="phone"
            required
          />
        </div>

        <div style="margin-top: 2em">
          <label for="city">City:</label>
          <input type="text" class="form-control" placeholder="Your city" name="locality" v-model="City" />
        </div>

        <div style="margin-top: 2em">
          <input type="checkbox" name="mail_list" id="mail_list" v-model="update_email_subscription" />
          <label for="mail_list" style="font-weight: normal"> Please keep me informed of EasyMedStat news.</label>
        </div>

        <div style="text-align: center">
          <button class="btn btn-primary" type="submit" style="margin-left: 1em" :disabled="disabled">
            Finalize my account
          </button>
        </div>
      </form>
    </div>
  </CenteredLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import PasswordInput from "@/components/PasswordInput.vue";
import Messages from "@/components/Messages.vue";
import CountrySelector from "./CountrySelector.vue";
import CenteredLayout from "./CenteredLayout.vue";
import router from "@/router/index";
import mixpanel from "@/ems/mixpanel";
import { WSClient, ApiMessage } from "@/ems/WSClient";
import { userSessionStore } from "@/store/user_session";
import { serieContextStore } from "@/store/serie_context";
import { HTMLInputElement } from "happy-dom";
import { LoggedUser } from "@/types/user";

export interface SharedUser {
  user_id: string;
  email_hash: string;
  Email: string;
  FirstName: string;
  has_password: boolean;
}

const props = defineProps<{
  initial_messages?: ApiMessage[];
  initial_email?: string;
  redir_on_success?: string;
  shared_user?: SharedUser | null;
}>();

const user_session = userSessionStore(),
  serie_context = serieContextStore();

const step = ref(1),
  email = ref(""),
  password = ref(""),
  is_email_exists = ref(false),
  is_password_bad = ref(false),
  tou = ref(false),
  FirstName = ref(""),
  LastName = ref(""),
  CountryCode = ref("FR"),
  City = ref(""),
  Profession = ref("NC"),
  phone = ref(""),
  update_email_subscription = ref(false),
  messages = ref<ApiMessage[]>([]),
  loading = ref(false),
  client = new WSClient(loading, messages);

let mixPanelInstance: any = null;

const first_name_input = ref<HTMLDivElement>();

// Watch for shared_user changes to initialize email and FirstName
watch(
  () => props.shared_user,
  (sharedUser) => {
    if (sharedUser) {
      email.value = sharedUser.Email;
      if (sharedUser.FirstName) {
        FirstName.value = sharedUser.FirstName;
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Initialize from cookies only if no shared_user
  if (!props.shared_user) {
    const ca = document.cookie.split(";");
    for (let cookie of ca) {
      while (cookie.charAt(0) == " ") {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf("creds") == 0) {
        let credsData = cookie.substring("creds".length + 1, cookie.length).split(":");
        email.value = credsData[0];
        password.value = credsData[1];
      }
    }
  }

  mixpanel.getMixpanelImpl(user_session.logged_user).then((mp) => {
    mixPanelInstance = mp;
    mp?.track("signup1-v2-shown");
  });
});

const on_email_change = async (e: Event): Promise<boolean> => {
  // Skip email check if this is a shared user (email is pre-filled and readonly)
  if (props.shared_user) return Promise.resolve(true);

  const input = e.target as HTMLInputElement | null;
  if (!input) return Promise.resolve(true);
  return await check_email(input.value);
};

const check_email = async (email_val: string): Promise<boolean> => {
  // Skip email check if this is a shared user (email is pre-filled and readonly)
  if (props.shared_user) {
    is_email_exists.value = false;
    return true;
  }

  const resp = await client.queryWs<{ exists: boolean }>("GET", "user_emailCheck", {
    email: email_val,
  });
  is_email_exists.value = resp.exists;
  return !resp.exists;
};

const checkFirstForm = async (e: Event) => {
  // Skip email check if this is a shared user
  if (!props.shared_user && !(await check_email(email.value))) return;
  step.value = 2;
  mixPanelInstance?.track("signup2-v2-details-shown");
  first_name_input.value?.focus();
};

const signUp = async (e: Event) => {
  user_session.reset_logout();

  e.preventDefault();

  if (FirstName.value.length < 2 || LastName.value.length < 2) return;
  loading.value = true;

  messages.value = [];

  let email_subscription = update_email_subscription.value
    ? ["system", "updates", "statistics", "partners"]
    : ["system"];

  // Use different API for shared users vs regular signups
  if (props.shared_user) {
    // Complete account for shared user - uses PHP session (no JWT)
    const complete_resp = await client.queryWs<{ success: boolean; logged: boolean; user_id: number }>(
      "POST",
      "user_completeAccount",
      {
        user_id: props.shared_user.user_id,
        hash: props.shared_user.email_hash,
      },
      {
        FirstName: FirstName.value,
        LastName: LastName.value,
        CountryCode: CountryCode.value,
        City: City.value,
        Profession: Profession.value,
        Pass: password.value,
        email_subscription: email_subscription,
        phone: phone.value,
      }
    );

    if (!complete_resp?.success || !complete_resp?.logged) {
      loading.value = false;
      return;
    }

    // After account completion, log in the user using their credentials to get a JWT
    const login_resp = await client.queryWs<{ jwt: string; logged_user: LoggedUser }>("POST", "session_login", null, {
      login: email.value,
      pass: password.value,
    });

    if (!login_resp?.jwt || !login_resp?.logged_user) {
      messages.value = [{ level: "error", text: "Account created but login failed. Please try logging in." }];
      loading.value = false;
      router.push({ name: "login" });
      return;
    }

    user_session.on_auth_success(login_resp.jwt, login_resp.logged_user);

    // Track in mixpanel
    if (mixPanelInstance) {
      mixPanelInstance.identify(complete_resp.user_id);
      mixPanelInstance.people.set({
        Email: email.value,
        "Registered on": new Date(),
        Profession: Profession.value,
        "Registration source": "Shared link",
      });
      mixPanelInstance.track("user-registered", {
        "Registration source": "Shared link",
        Email: email.value,
      });
    }

    const ug = (<any>window).userGuiding;
    if (ug)
      ug.identify(complete_resp.user_id, {
        email: email.value,
      });

    const vueRedir = sessionStorage.getItem("afterlogin");
    if (vueRedir) {
      try {
        const parsed_route = JSON.parse(vueRedir);
        parsed_route["query"] = { ...parsed_route?.query, action: "signup" };
        await router.push(parsed_route);
      } catch (e) {
        console.error("Error navigating to saved route after signup", e);
        sessionStorage.removeItem("afterlogin");
        router.push({ name: "SeriesList" });
      }
    } else {
      router.push({ name: "SeriesList" });
    }
    return;
  }

  // Regular signup - uses JWT
  const register_resp = await client.queryWs<{ jwt: string; logged_user: LoggedUser }>(
    "POST",
    "session_register",
    null,
    {
      Email: email.value,
      FirstName: FirstName.value,
      LastName: LastName.value,
      CountryCode: CountryCode.value,
      City: City.value,
      Profession: Profession.value,
      Pass: password.value,
      email_subscription: email_subscription,
      url_referer: document.referrer,
      phone: phone.value,
    }
  );

  const logged_user = register_resp?.logged_user;

  if (!logged_user?.Email) {
    alert("Registration error");
    return;
  }

  user_session.on_auth_success(register_resp?.jwt, register_resp?.logged_user);

  email.value = logged_user.Email;
  const ug = (<any>window).userGuiding;
  if (ug)
    ug.identify(logged_user.UserId, {
      email: logged_user.Email,
    });

  if (mixPanelInstance) {
    mixPanelInstance.identify(logged_user.UserId);
    mixPanelInstance.people.set({
      Email: email.value,
      "Registered on": new Date(),
      Profession: Profession.value,
      "Registration source": "Signup v2",
    });
    mixPanelInstance.track("user-registered", {
      "Registration source": "Signup v2",
      Email: email.value,
    });
  }

  // Use sessionStorage afterlogin if available
  const vueRedir = sessionStorage.getItem("afterlogin");
  if (vueRedir) {
    try {
      const parsed_route = JSON.parse(vueRedir);
      parsed_route["query"] = { ...parsed_route?.query, action: "signup" };
      await router.push(parsed_route);
    } catch (e) {
      console.error("Error navigating to saved route after signup", e);
      sessionStorage.removeItem("afterlogin");
      router.push({ name: "onboarding_welcome" });
    }
  } else {
    const referer = logged_user.url_referer;
    // We need some business specs to continue
    if (referer !== "kaplan-meier") {
      router.push({ name: "onboarding_welcome" });
    }
  }
};

const password_recall = function () {
  router.push({ name: "ask_password", params: { email: email.value } });
};

const disabled = computed(() => {
  return loading.value || !password.value || !email.value || FirstName.value.length < 2 || LastName.value.length < 2;
});

watch(email, (newVal: string) => {
  // Skip email check if this is a shared user
  if (props.shared_user) return;
  if (newVal) {
    check_email(newVal);
  }
});
</script>

<style scoped>
#signin {
  display: flex;
}

#signin > div {
  margin: 4rem auto;
  max-width: 45em;
  text-align: left;
}

#signin h1 {
  font-size: 3em;
  margin-bottom: 1em;
}

#signin ul.points {
  list-style: none;
  font-size: 1.5em;
  padding: 0;
  line-height: 3em;
}

#signin .box {
  padding: 4em 5em;
}

#signin h2 {
  font-size: 2.14em;
}

#signin label {
  display: inline-block;
  margin-bottom: 5px;
  font-weight: bold;
}

#signin form > div {
  margin-bottom: 2em;
}

#signin form > div:last-child {
  margin-bottom: 0;
}

#signin .points svg {
  color: var(--green-main-color);
}

#signin input.error {
  border: 2px solid var(--red-danger-color);
}

#signin :deep(.input-warn.fa-exclamation-circle) {
  position: absolute;
  left: -1.5em;
  font-size: 1.8em;
  top: 0.2em;
  color: var(--red-danger-color);
}

.error {
  margin-top: 0.8em;
  color: var(--red-danger-color);
}

.readonly-input {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.shared-user-info {
  margin-top: 0.8em;
  color: var(--blue-info-color, #17a2b8);
  font-size: 0.9em;
}
</style>
